/**
 * Shared 2D projection, uniform scaling, and segment-label geometry for
 * Schließprofil previews (PDF + Aluminium DXF).
 */

import { Sides } from "@project/emerge-types";

export const PROFILE_PREVIEW_MM_TO_PT = 2.83465;

export const PROFILE_PREVIEW_OUTER_PADDING_PT = 4;
export const PROFILE_PREVIEW_INNER_TEXT_PADDING_PT = 10;
export const PROFILE_PREVIEW_SEGMENT_LABEL_OFFSET_PT = 7;

export type ProfileAxis = "x" | "y" | "z";

const PROJECTION_PLANES: Record<
  Sides,
  { horizontal: ProfileAxis; vertical: ProfileAxis }
> = {
  [Sides.LEFT]: { horizontal: "z", vertical: "y" },
  [Sides.RIGHT]: { horizontal: "z", vertical: "y" },
  [Sides.BACK]: { horizontal: "x", vertical: "y" },
  [Sides.FRONT]: { horizontal: "x", vertical: "y" },
  [Sides.BOTTOM]: { horizontal: "x", vertical: "z" },
  [Sides.TOP]: { horizontal: "x", vertical: "z" },
};

function getCoordinate(
  axis: ProfileAxis,
  coords: [number, number, number],
): number {
  const [cx, cy, cz] = coords;
  switch (axis) {
    case "x":
      return cx;
    case "y":
      return cy;
    case "z":
      return cz;
  }
}

/**
 * Project 3D profile points to 2D (same plane rules as PDF profile preview).
 */
export function projectProfilePointsTo2D(
  points: Array<[number, number, number]>,
  cutReference: number | undefined,
): Array<[number, number]> {
  const side =
    cutReference === undefined ? Sides.FRONT : (cutReference as Sides);
  const plane = PROJECTION_PLANES[side] ?? { horizontal: "x", vertical: "y" };

  return points.map(([x, y, z]) => {
    const h = getCoordinate(plane.horizontal, [x, y, z]);
    const v = getCoordinate(plane.vertical, [x, y, z]);
    return [h, v];
  });
}

export function projectVector3ProfileTo2D(
  points: ReadonlyArray<{ x: number; y: number; z: number }>,
  cutReference: Sides,
): Array<[number, number]> {
  return projectProfilePointsTo2D(
    points.map((p) => [p.x, p.y, p.z]),
    cutReference,
  );
}

export interface BoundingBox2D {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
}

export function boundingBoxFrom2DProfiles(
  allPoints2D: ReadonlyArray<{ points: ReadonlyArray<[number, number]> }>,
): BoundingBox2D {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const { points } of allPoints2D) {
    for (const [px, py] of points) {
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/** Uniform scale to fit bbox into maxW × maxH (same units as bbox). */
export function calculateScale(
  bboxWidth: number,
  bboxHeight: number,
  maxW: number,
  maxH: number,
): number {
  if (bboxWidth <= 0 && bboxHeight <= 0) return 1;
  if (bboxWidth <= 0) return maxH / bboxHeight;
  if (bboxHeight <= 0) return maxW / bboxWidth;
  return Math.min(maxW / bboxWidth, maxH / bboxHeight);
}

export function distance2D(p1: [number, number], p2: [number, number]): number {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  return Math.hypot(dx, dy);
}

export interface TextAngleResult {
  angleRad: number;
  angleDeg: number;
  flipOffset: boolean;
}

/**
 * Text rotation for segment labels (readability: avoid upside-down text).
 */
export function calculateTextAngle(dx: number, dy: number): TextAngleResult {
  let angleRad = -Math.atan2(dy, dx);
  let angleDeg = (angleRad * 180) / Math.PI;

  if (!Number.isFinite(angleDeg)) {
    return { angleRad: 0, angleDeg: 0, flipOffset: false };
  }

  const flipOffset = true;
  if (angleDeg > 90) {
    angleDeg -= 180;
    angleRad -= Math.PI;
  } else if (angleDeg < -90) {
    angleDeg += 180;
    angleRad += Math.PI;
  }

  return { angleRad, angleDeg, flipOffset };
}

/**
 * Label insertion point (PDF drawText origin) with perpendicular offset from segment mid.
 */
export function calculateTextPosition(params: {
  midX: number;
  midY: number;
  perpX: number;
  perpY: number;
  textWidth: number;
  fontSize: number;
  angleRad: number;
  offset: number;
}): { x: number; y: number } | null {
  const { midX, midY, perpX, perpY, textWidth, fontSize, angleRad, offset } =
    params;
  const desiredX = midX + perpX * offset;
  const desiredY = midY + perpY * offset;

  if (
    Number.isNaN(desiredX) ||
    Number.isNaN(desiredY) ||
    Number.isNaN(angleRad)
  ) {
    return null;
  }

  const textCenterOffsetX = textWidth / 2;
  const textCenterOffsetY = fontSize / 2.5;
  const cosAngle = Math.cos(angleRad);
  const sinAngle = Math.sin(angleRad);

  const rotatedOffsetX =
    textCenterOffsetX * cosAngle - textCenterOffsetY * sinAngle;
  const rotatedOffsetY =
    textCenterOffsetX * sinAngle + textCenterOffsetY * cosAngle;

  return {
    x: desiredX - rotatedOffsetX,
    y: desiredY - rotatedOffsetY,
  };
}

/**
 * Inner drawable size in mm — matches PDF padding in profilePreviewSchema.
 */
export function profilePreviewInnerSizeMm(
  schemaWidthMm: number,
  schemaHeightMm: number,
): { maxW: number; maxH: number } {
  const groupWidthPt =
    schemaWidthMm * PROFILE_PREVIEW_MM_TO_PT -
    2 * PROFILE_PREVIEW_OUTER_PADDING_PT;
  const groupHeightPt =
    schemaHeightMm * PROFILE_PREVIEW_MM_TO_PT -
    2 * PROFILE_PREVIEW_OUTER_PADDING_PT;
  const innerWPt = groupWidthPt - 2 * PROFILE_PREVIEW_INNER_TEXT_PADDING_PT;
  const innerHPt = groupHeightPt - 2 * PROFILE_PREVIEW_INNER_TEXT_PADDING_PT;
  return {
    maxW: innerWPt / PROFILE_PREVIEW_MM_TO_PT,
    maxH: innerHPt / PROFILE_PREVIEW_MM_TO_PT,
  };
}

/** Perpendicular offset for segment labels, in layout mm (PDF 7 pt). */
export function profilePreviewSegmentLabelOffsetMm(): number {
  return PROFILE_PREVIEW_SEGMENT_LABEL_OFFSET_PT / PROFILE_PREVIEW_MM_TO_PT;
}

/**
 * Map projected 2D mm (u,v) to layout mm; same relative placement as PDF
 * (centered in schema, vertical axis flipped like pdf-lib Y).
 */
export function layoutPointFromProfile2D(params: {
  u: number;
  v: number;
  bboxCenterU: number;
  bboxCenterV: number;
  scale: number;
  schemaWidthMm: number;
  schemaHeightMm: number;
}): { x: number; y: number } {
  const {
    u,
    v,
    bboxCenterU,
    bboxCenterV,
    scale,
    schemaWidthMm,
    schemaHeightMm,
  } = params;
  return {
    x: schemaWidthMm / 2 + (u - bboxCenterU) * scale,
    y: schemaHeightMm / 2 - (v - bboxCenterV) * scale,
  };
}

/** Rough Helvetica-like width for segment length digits (PDF parity for DXF). */
export function estimateSegmentLabelTextWidth(
  charCount: number,
  fontSize: number,
): number {
  return Math.max(charCount, 1) * 0.55 * fontSize;
}

/**
 * MakerJS caption anchor: midpoint at label position, direction = readable text.
 */
export function makerJsCaptionAnchorAlongReadableText(params: {
  labelCenterX: number;
  labelCenterY: number;
  angleRad: number;
  halfChordMm?: number;
}): { origin: [number, number]; end: [number, number] } {
  const { labelCenterX, labelCenterY, angleRad } = params;
  const half = params.halfChordMm ?? 5;
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return {
    origin: [labelCenterX - c * half, labelCenterY - s * half],
    end: [labelCenterX + c * half, labelCenterY + s * half],
  };
}
